import React, { useEffect, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import AppleLogin from "react-apple-login";
import Logo from "../../assets/Logo.jpeg";
import { WEB_BASE_URL } from "../../config/baseUrls";

const ReferralLandingPage = () => {
  const [referralCode, setReferralCode] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authProvider, setAuthProvider] = useState(null);

  const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID;
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");

    if (ref) {
      setReferralCode(ref);
      localStorage.setItem("referralCode", ref);
    }
  }, []);

  const redirectToAppStore = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (/android/i.test(userAgent)) {
      window.location.href =
        "https://play.google.com/store/apps/details?id=com.otanative.ota";
    } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      window.location.href =
        "https://apps.apple.com/in/app/ota-life/id6743555476";
    } else {
      window.location.href = "https://be-native.life/download";
    }
  };

  const sendTokenToBackend = async (idToken, provider) => {
    try {
      setLoading(true);
      setAuthProvider(provider);

      const payload = {
        token: idToken,
      };

      if (referralCode) {
        payload.referralCodeProvided = referralCode;
      }

      const response = await fetch(`${BACKEND_URL}/${provider}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Backend authentication failed");
      }

      const data = await response.json();

      localStorage.setItem("JWTtoken", data.token);
      localStorage.setItem("userId", data.user.cuid);
      redirectToAppStore();
    } catch (error) {
      console.error(`${provider} authentication error:`, error);
      setError(
        `${
          provider.charAt(0).toUpperCase() + provider.slice(1)
        } authentication failed`,
      );
    } finally {
      setLoading(false);
      setAuthProvider(null);
    }
  };

  const handleGoogleSuccess = (credentialResponse) => {
    sendTokenToBackend(credentialResponse.credential, "google");
  };

  const handleGoogleError = () => {
    setError("Google authentication failed. Please try again.");
    setLoading(false);
    setAuthProvider(null);
  };

  const handleAppleSuccess = (response) => {
    if (response.error || !response.authorization) {
      console.warn("Apple login was cancelled or failed:", response);
      setLoading(false);
      setAuthProvider(null);
      return;
    }

    sendTokenToBackend(response.authorization.id_token, "apple");
  };

  const handleAppleError = (error) => {
    console.error("Apple authentication error:", error);
    setError("Apple authentication failed. Please try again.");
    setLoading(false);
    setAuthProvider(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4 font-sans">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 space-y-6 text-center">
        <img
          src={Logo}
          alt="Ota Logo"
          className="w-20 h-20 rounded-xl mx-auto mb-4 object-cover shadow-sm"
        />

        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">
            You've been invited!
          </h1>
          <p className="mt-2 text-gray-500 text-sm">
            Join Ota today and claim your exclusive welcome reward.
          </p>
        </div>

        {referralCode && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-6">
            <p className="text-amber-800 font-semibold text-lg flex items-center justify-center gap-2">
              <span>🎁</span> Get 5000 Free Points
            </p>
            <p className="text-amber-600 text-xs mt-1">
              Code applied:{" "}
              <span className="font-mono font-bold">{referralCode}</span>
            </p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="login-buttons">
          <div className="google-login-wrapper">
            {loading && authProvider === "google" ? (
              <button className="login-btn google-btn" disabled>
                <div className="btn-loader"></div>
              </button>
            ) : (
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap={false}
                theme="outline"
                size="large"
                text="continue_with"
                shape="rectangular"
                logo_alignment="left"
              />
            )}
          </div>

          {loading && authProvider === "apple" ? (
            <button className="login-btn apple-btn" disabled>
              <div className="btn-loader"></div>
            </button>
          ) : (
            <AppleLogin
              clientId={APPLE_CLIENT_ID}
              redirectURI={`${WEB_BASE_URL}/invite`}
              scope="email name"
              responseMode="form_post"
              usePopup={true}
              callback={handleAppleSuccess}
              onError={handleAppleError}
              render={(props) => (
                <button {...props} className="login-btn apple-btn">
                  <svg
                    className="btn-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  <span>Continue with Apple</span>
                </button>
              )}
            />
          )}
        </div>

        <div className="pt-6 text-xs text-gray-400">
          By continuing, you agree to Ota's Terms of Service and Privacy Policy.
        </div>
      </div>
    </div>
  );
};

export default ReferralLandingPage;
